import type { PartnerCompany } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import type { KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import { zodPartnerCompanySchema } from './validations';

/**
 * Fetches a single partner company by ID.
 *
 * @param companyId - The UUID of the company to fetch.
 * @param options - Optional request options (e.g. abort signal).
 * @returns The partner company data.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/get_partner_company_by_id_data_exchange_companies__company_id__get
 */
export const fetchPartnerCompany = async (
  companyId: UUID,
  options?: KyRequestOptions,
): Promise<PartnerCompany> => {
  const response = await vestigasApi
    .get(ENDPOINT.DATA_EXCHANGE.GET_DATA_EXCHANGE_COMPANY(companyId), options)
    .json<PartnerCompany>();

  const validatedResponse = zodPartnerCompanySchema.safeParse(response);

  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Partner company API response has validation errors',
      error: {
        errors: validatedResponse.error.issues,
        errorMessage: validatedResponse.error.message,
        response,
      },
    });
  }

  return response;
};
